import React, { useState } from 'react';
import { withRouter } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { message } from 'antd';
import { graphql } from 'react-apollo';
import compose from 'lodash/flowRight';
import { Lock, User, KeyRound } from 'lucide-react';

import UPDATE_USER from '../../../.././core/GraphQl/Mutations/UPDATE_USER';
import { Button } from '@/components/ui/button';
import AuthShell from './AuthShell';
import Field from './Field';
import ToggleVisibility from './ToggleVisibility';

const USERNAME_PATTERN = /^[A-Za-z0-9]+$/;
const PASSWORD_PATTERN =
  /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[!@#$%^&*()_+=[{\]};:<>|./?,-]).{8,}$/;
const PASSWORD_MESSAGE =
  'Password must have a mix of upper and lowercase letters, numbers and special characters. eg: Test12345@#';

const RecoverPasswordForm = (props) => {
  const [username, setUsername] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [verifyPassword, setVerifyPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [showNew, setShowNew] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validateUsername = () => {
    const next = {};
    if (!username) next.username = 'Username is required';
    else if (!USERNAME_PATTERN.test(username))
      next.username = 'User name must be all one word and not contain spaces';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const validateReset = () => {
    const next = {};
    if (!code) next.code = 'Code is required';
    if (!newPassword) next.newpassword = PASSWORD_MESSAGE;
    else if (!PASSWORD_PATTERN.test(newPassword)) next.newpassword = PASSWORD_MESSAGE;
    if (!verifyPassword) next.verifypassword = PASSWORD_MESSAGE;
    else if (!PASSWORD_PATTERN.test(verifyPassword))
      next.verifypassword = PASSWORD_MESSAGE;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const forgotPassword = () => {
    if (!validateUsername()) return;
    setLoading(true);
    Auth.forgotPassword(username)
      .then(() => {
        setLoading(false);
        setSubmitted(true);
        message.success('Recovery code sent.');
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
        message.warning('Invalid user.');
      });
  };

  const forgotPasswordSubmit = () => {
    if (!validateReset()) return;
    if (newPassword !== verifyPassword) {
      message.warning('Passwords do not match');
      return;
    }
    setLoading(true);
    Auth.forgotPasswordSubmit(username, code, newPassword)
      .then(() => {
        setLoading(false);
        message.success('Password changed. Please log in again');
        props.history.push('/signin');
      })
      .catch(() => {
        setLoading(false);
        message.warning('Invalid code.');
      });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (submitted) forgotPasswordSubmit();
    else forgotPassword();
  };

  return (
    <AuthShell
      title="Recover password"
      subtitle={
        submitted
          ? 'Enter the code we sent and choose a new password.'
          : 'Enter your username and we will send a recovery code.'
      }
      onLogoClick={() => props.history.push('/')}
    >
      <form onSubmit={onSubmit} noValidate>
        <div className="space-y-4">
          <Field
            id="recover-username"
            label="Username"
            icon={User}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            error={errors.username}
            autoComplete="username"
            placeholder="yourusername"
          />

          {submitted && (
            <>
              <Field
                id="recover-code"
                label="Recovery code"
                icon={KeyRound}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                error={errors.code}
                autoComplete="one-time-code"
                inputMode="numeric"
                placeholder="123456"
              />

              <Field
                id="recover-newpassword"
                label="New password"
                icon={Lock}
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                error={errors.newpassword}
                autoComplete="new-password"
                placeholder="••••••••"
                rightAdornment={
                  <ToggleVisibility shown={showNew} onClick={() => setShowNew((s) => !s)} />
                }
              />

              <Field
                id="recover-verifypassword"
                label="Verify new password"
                icon={Lock}
                type={showVerify ? 'text' : 'password'}
                value={verifyPassword}
                onChange={(e) => setVerifyPassword(e.target.value)}
                error={errors.verifypassword}
                autoComplete="new-password"
                placeholder="••••••••"
                rightAdornment={
                  <ToggleVisibility shown={showVerify} onClick={() => setShowVerify((s) => !s)} />
                }
              />
            </>
          )}
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="mt-6 w-full bg-[#1890ff] hover:bg-[#096dd9]"
        >
          {loading ? 'Working…' : submitted ? 'Change password' : 'Recover password'}
        </Button>
      </form>
    </AuthShell>
  );
};

export default compose(graphql(UPDATE_USER, { name: 'updateUser' }))(
  withRouter(RecoverPasswordForm)
);
